<template>
  <div>
    <v-skeleton-loader v-if="loading" type="article"></v-skeleton-loader>
    <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'" class="mt-2" elevation="0"
      v-if="transaction && transactionItems">
      <v-alert type="warning" v-if="isIncompleteCustomAch">
        You have until <b>{{ $d(isIncompleteCustomAch, "month_day_year", $i18n.locale) }}</b> to complete this
        transaction.
        After 24 hours, the transaction will automatically cancel
        itself. Please use Instant Verification only. Micro-deposits are not available at this time. If you have any
        questions, please contact your academy.
      </v-alert>
      <v-list>
        <template v-for="(i, index) in transactionItems">
          <v-list-item :key="'item' + index">
            <v-list-item-avatar>
              <v-avatar size="50" color="grey lighten-4">
                <img v-if="i.product && i.product.image" :src="userImg(i.product.image)" />
                <img v-else-if="academy.image" :src="userImg(academy.image)" />
                <img v-else :src="assets('logo-circular-watermarked.png')" />
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title two-line v-if="i.product">
                {{ i.product.name }}
                <span class="ml-1 gray--text">(x{{ i.quantity }})</span>
              </v-list-item-title>
              <v-list-item-title two-line v-else>
                {{ i.name }}
                <span class="ml-2">(x{{ i.quantity }})</span>
              </v-list-item-title>
              <div v-if="i.product && i.product.interval">
                <v-list-item-subtitle>
                  <v-icon size="15" class="mr-1">mdi-autorenew</v-icon>
                  {{ displayInterval(i.product.interval) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="transaction.subscriptionStartDate" class="primary--text">
                  <span class="mr-1">{{ $t('message.starts') }}</span>
                  {{ $d(new Date(transaction.subscriptionStartDate), 'month_day_year', $i18n.locale) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="transaction.product && transaction.product.description">
                  {{ transaction.product.description }}
                </v-list-item-subtitle>
              </div>
            </v-list-item-content>
            <v-list-item-action v-if="isNaN(i.amountDecimal) && i.product">
              <span class="title-amount">{{ currencySymbol + i.product.amountDecimal }}</span>
            </v-list-item-action>
            <v-list-item-action v-else-if="i.amountDecimal >= 0">
              <span v-if="i.amountDecimal > 0" class="title-amount">{{ currencySymbol + i.amountDecimal }}</span>
              <v-chip small v-else color="secondary">{{ $t('message.free') }}</v-chip>
            </v-list-item-action>
            <v-list-item-action v-else>
              <v-icon color="primary">mdi-hand-coin-outline</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="index + 1 < transaction.items.length" :key="index"></v-divider>
        </template>
        <div class="text-right mr-2">
          TOTAL: {{ transaction.managedAcademy ? transaction.managedAcademy.currencySymbol : '$' }}
          {{ Math.abs(totalAmount) }}
        </div>
      </v-list>
    </v-sheet>
    <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'" class="mt-2" v-if="academy && transaction">
      <v-alert v-if="payingLateMessage" color="gold" type="warning">{{ payingLateMessage }}</v-alert>
      <v-list>
        <v-list-item two-line @click="goToAcademy(academy._id)">
          <v-list-item-content>
            <v-list-item-title>{{ academy.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.vendor') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item two-line v-if="transaction.buyer._id != $store.state.user._id">
          <v-list-item-content>
            <v-list-item-title>{{ transaction.buyer.displayName }}</v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.family_member') }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-avatar>
            <UserAvatar :user="transaction.buyer"></UserAvatar>
          </v-list-item-avatar>
        </v-list-item>
        <v-divider v-if="transaction.buyer._id != $store.state.user._id"></v-divider>
        <v-list-item two-line>
          <v-list-item-content>
            <v-list-item-title>{{ $d(new Date(transaction.time), 'long', $i18n.locale) }}</v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.date') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider v-if="transaction.subscriptionStartDate"></v-divider>
        <v-list-item two-line v-if="transaction.subscriptionStartDate">
          <v-list-item-content>
            <v-list-item-title>{{ $d(new Date(transaction.subscriptionStartDate), 'month_day_year', $i18n.locale) }}
            </v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.starts_on') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item two-line v-if="transaction.user">
          <v-list-item-content>
            <v-list-item-title>{{ transaction.user.fName }} {{ transaction.user.lName }}</v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.processed_by') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item two-line v-if="!transaction.items">
          <v-list-item-content>
            <v-list-item-title>{{ transaction.quantity }}</v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.quantity') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider v-if="transaction.prorated && transaction.prorated.amount"></v-divider>
        <v-list-item two-line v-if="transaction.prorated && transaction.prorated.amount">
          <v-list-item-content>
            <v-list-item-title>{{ currencySymbol }}{{ transaction.prorated.amount }}
              <span class="ml-1">- {{ transaction.prorated.days }} {{ $t('message.days') }}</span>
            </v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.prorated_amount') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item two-line>
          <v-list-item-content>
            <v-list-item-title>
              <span v-html="getPaymentStatus(transaction, subscriptionStatus)"></span>
              <span class="ml-1">
                <DesktopComputerIcon class="hero-icon" :color="getStatusColor(transaction, subscriptionStatus)"
                  v-if="transaction.processedBy == 'desk'" size="20"></DesktopComputerIcon>
                <DeviceMobileIcon class="hero-icon" :color="getStatusColor(transaction, subscriptionStatus)" v-else
                  size="20"></DeviceMobileIcon>
              </span>
            </v-list-item-title>
            <v-list-item-subtitle>{{ $t('message.status') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item two-line v-if="transaction.subscriptionCheckins > 0 || transaction.subscriptionEndDate">
          <v-list-item-content>
            <v-list-item-title :class="transaction.expired ? 'red--text' : ''">
              <div v-if="transaction.subscriptionCheckins > 0">
                <span>
                  {{ $t('message.allowed') }} {{ transaction.subscriptionCheckins }} checkins
                </span>
                <span class="ml-1 gray--color"> ({{ $t('message.checkins_left', [getCheckinsLeft()]) }})</span>
              </div>
              <div v-if="transaction.subscriptionEndDate">
                {{ $d(new Date(transaction.subscriptionEndDate), 'long', $i18n.locale) }}
              </div>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $t('message.expires') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item two-line v-if="transaction.coupon">
          <v-list-item-content>
            <v-list-item-title v-if="transaction.coupon.discountPercent">
              {{ transaction.coupon.discountPercent }}%
              <span class="ml-1" v-if="transaction.coupon.expirationDate">({{ $t('message.expires') }} {{ $d(new
                Date(transaction.coupon.expirationDate),
                'month_day_year', $i18n.locale) }})</span>
            </v-list-item-title>
            <v-list-item-title v-else-if="transaction.coupon.discountAmount">
              {{ currencySymbol }}{{ transaction.coupon.discountAmount }}
              <span class="ml-1" v-if="transaction.coupon.expirationDate">({{ $t('message.expires') }} {{ $d(new
                Date(transaction.coupon.expirationDate),
                'month_day_year', $i18n.locale) }})</span>
            </v-list-item-title>
            <v-list-item-subtitle>
              <TagIcon class="hero-icon" size="15" :color="themeColor('primary')"></TagIcon>
              {{ $t('message.discount') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item three-line>
          <v-list-item-content>
            <v-list-item-title>
              <span v-if="transaction.coupon && transaction.coupon.discountedTotal > 0">
                {{ currencySymbol }}
                {{ transaction.coupon.discountedTotal }}
              </span>
              <span v-else>
                {{ transaction.managedAcademy.currencySymbol }}{{ Math.abs(totalAmount) }}
              </span>
            </v-list-item-title>
            <v-list-item-subtitle>
              Total
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-caption" v-if="taxData && taxData.enabled">
              <span class="mr-1">{{ $t("message.tax") }}:</span>
              <span>{{ currencySymbol }}{{ Math.abs(centsToAmount(taxData.totalDetails.amount_tax)) }}</span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-sheet>
    <v-sheet v-if="paymentMethod && !transaction.cancelled && transaction.completed"
      :rounded="isSmallerDisplay() ? '' : 'lg'" class="mt-2 pa-2">
      <v-subheader>
        <CreditCardIcon class="hero-icon"></CreditCardIcon>
        {{ $t('message.payment_method') }}
        <v-spacer></v-spacer>
        <v-btn v-if="!this.isCustomAchTransaction(transaction)" @click="updateCreditCard()" elevation="0" small
          color="primary" outlined>{{ $t('message.update')
          }}</v-btn>
      </v-subheader>
      <PaymentMethod :height="60" :payment-method="paymentMethod" :hide-name="true"></PaymentMethod>
    </v-sheet>
    <v-sheet v-if="transaction && transaction.paidWithCash" :rounded="isSmallerDisplay() ? '' : 'lg'" class="mt-2 pa-2">
      <v-card elevation="0">
        <v-subheader>
          <v-icon class="mr-1" v-if="!transaction.receipt">mdi-receipt-text-outline</v-icon>
          <v-icon class="mr-1" v-else>mdi-receipt-text-check-outline</v-icon>
          {{ $t('message.receipt') }}
        </v-subheader>
        <div v-if="uploadingFile" class="mt-2">
          <v-progress-linear buffer-value="5" color="success" height="15" stream :value="uploadPercentage">
          </v-progress-linear>
        </div>
        <input style="display: none" type="file" accept="image/*" @change="onPhotoFileSelect" ref="fileUploadInput" />
        <div v-if="uploadedFile" class="text-center">
          <div>
            <ImageDisplay style="width: 80%;" :src="uploadedFile"></ImageDisplay>
          </div>
          <div v-if="uploadedFile">
            <v-btn block color="secondary" small :loading="confirmingReceipt" @click="confirmUploadedReceipt()"
              class="mb-1">
              {{ $t('message.confirm') }}
            </v-btn>
            <v-btn block small @click="removeReceipt()">
              {{ $t('message.cancel') }}
            </v-btn>
          </div>
        </div>
        <v-card-actions v-if="!uploadedFile">
          <v-btn block outlined color="primary" small :loading="uploadingFile" :disabled="uploadingFile"
            @click="filePicker()">
            {{ $t('message.upload') }}
          </v-btn>
        </v-card-actions>
        <div v-if="transaction.receipt">
          <div class="text-center">
            <ImageDisplay style="width: 100%;" :src="transaction.receipt"></ImageDisplay>
          </div>
          <div class="mt-1 text-center">
            <v-btn elevation="0" small color="error" block @click="removeReceipt(true)">
              <v-icon class="mr-1">mdi-trash-can-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-sheet>
    <v-sheet v-if="transaction && !transaction.completed && !transaction.cancelled"
      :rounded="isSmallerDisplay() ? '' : 'lg'" class="mt-2 pa-2">
      <v-alert type="warning" v-if="transaction.subscriptionStartDate && isCustomAchTransaction(transaction)">
        When setting up a debit payment for a membership that starts in the future, <b>a one-time {{ currencySymbol
          }}1</b> charge
        will
        occur at the time you enter the bank information.
        This is required to comply with mandates due to banking regulations in your country.
      </v-alert>
      <v-card-text v-if="transaction.paymentType == 'nz_bank_account'">
        <v-card outlined elevation="0">
          <v-card-text class="text-center">
            <div class="text-overline">11-0000-00000000-10</div>
            <div class="text-caption">Bank Code - Branch Code - Account Number - Suffix</div>
          </v-card-text>
          <v-card-text>
            <v-text-field outlined dense label="Bank Code" v-model="customAchPostData.bank_code">
            </v-text-field>
            <v-text-field outlined dense label="Branch Code" v-model="customAchPostData.branch_code">
            </v-text-field>
            <v-text-field outlined dense label="Account Number" v-model="customAchPostData.account_number">
            </v-text-field>
            <v-text-field hide-details outlined dense label="Suffix" v-model="customAchPostData.suffix">
            </v-text-field>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <div v-if="session && session.status == 'active'">
          <v-btn block outlined :disabled="true" elevation="0" color="primary">
            {{ $t('message.already_completed') }}
          </v-btn>
        </div>
        <v-btn block v-else-if="pendingUpdateCompletion" :loading="handlingCharges"
          :disabled="allowsBankPayment && transaction.paymentTypes.length == 1 && $route.query.inApp ? true : false"
          elevation="0" @click="completeCreditCardUpdate()" color="primary" outlined>
          {{ $t('message.finish_payment_in_progress') }}
        </v-btn>
        <v-btn block v-else :loading="handlingCharges"
          :disabled="allowsBankPayment && transaction.paymentTypes.length == 1 && $route.query.inApp ? true : false"
          elevation="0" @click="completeCheckout()" color="primary" outlined>
          <span v-if="transaction.subscriptionStartDate">{{ $t('message.enter_payment_information') }}</span>
          <span v-else>{{ $t('message.complete_payment') }}</span>
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
      <v-card-text>
        <v-alert outlined type="warning"
          v-if="transaction.subscriptionStartDate && transaction.paymentType == 'nz_bank_account'">
          A one-time<span class="text-h6"><b> {{ currencySymbol }}1</b></span> payment be processed upon completion.
        </v-alert>
        <v-alert outlined type="warning" v-if="mustUseBrowserToComplete">
          {{ $t('message.bank_payment_completion_browser') }}
        </v-alert>
        <v-alert outlined type="warning" v-if="pendingUpdateCompletion">
          {{ $t('message.finish_payment_in_progress_explained') }}
        </v-alert>
      </v-card-text>
    </v-sheet>
    <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'" v-if="invoices" class="mt-2">
      <v-card-text>
        <v-list>
          <v-subheader>
            <ReceiptTaxIcon class="hero-icon"></ReceiptTaxIcon>
            {{ $t('message.invoices') }}
          </v-subheader>
          <v-list-item v-if="upcomingInvoice">
            <v-list-item-action>
              <v-icon>mdi-sync</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title v-if="upcomingInvoice.created">
                {{ $d(unixString2Datetime(upcomingInvoice.created), 'month_day_year', $i18n.locale) }}
              </v-list-item-title>
              <v-list-item-title v-else-if="upcomingInvoice.dueDate">
                {{ $d(new Date(upcomingInvoice.dueDate), 'month_day_year', $i18n.locale) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ currencySymbol }}{{ upcomingInvoice.amountPaid }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <template v-for="(i, index) in invoices">
            <v-list-item :key="'invoice' + index">
              <v-list-item-action>
                <CreditCardIcon :color="i.paid ? 'green' : 'red'" size="25">card_membership</CreditCardIcon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title v-if="i.created">
                  {{ $d(unixString2Datetime(i.created), 'month_day_year', $i18n.locale) }}
                </v-list-item-title>
                <v-list-item-title v-else-if="i.dueDate">
                  {{ $d(new Date(i.dueDate), 'month_day_year', $i18n.locale) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ currencySymbol }}{{ i.amountPaid }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-content>{{ $t('message.attempts') }}: {{ i.attempts }}</v-list-item-content>
              <v-list-item-action class="text-left">
                <v-btn small icon v-if="i.refundedAmount != i.amountPaid && i.paymentIntent"
                  @click="sendEmailDialog = true; selectedInvoice = i.paymentIntent;">
                  <v-icon>mdi-receipt-send-outline</v-icon>
                </v-btn>
                <v-btn small icon v-if="transaction.paymentsModule == 'easypay'"
                  @click="sendEmailDialog = true; selectedInvoice = i;">
                  <v-icon>mdi-receipt-send-outline</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index + 1 < invoices.length" :key="index"></v-divider>
          </template>
        </v-list>
      </v-card-text>
    </v-sheet>
    <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'" class="mt-2"
      v-if="transaction && transaction.checkins && transaction.checkins.length > 0">
      <div>
        <v-subheader>
          <LocationMarkerIcon class="hero-icon"></LocationMarkerIcon>
          {{ $t('message.checkins') }}
        </v-subheader>
        <template v-for="(i, index) in transaction.checkins">
          <v-list-item :key="'checkin' + index">
            <v-list-item-avatar>
              <LocationMarkerIcon class="hero-icon" size="25"></LocationMarkerIcon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                <span>{{ i.name }}</span>
              </v-list-item-title>
              <v-list-item-subtitle v-if="i.date">{{ $d(new Date(i.date), 'long', $i18n.locale) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-if="index + 1 < transaction.checkins.length" :key="index"></v-divider>
        </template>
      </div>
    </v-sheet>
    <v-sheet class="mt-2" :rounded="isSmallerDisplay() ? '' : 'lg'" v-if="transaction">
      <v-card-actions v-if="!isSmallerDisplay()">
        <v-btn v-if="transaction.completed && (!invoices || invoices.length == 0)" small elevation="0"
          @click="sendEmailDialog = true;" color="primary" outlined>
          {{ $t("message.email_receipt") }}
        </v-btn>
        <v-btn
          v-if="!hidePauseButton && transaction.completed && !transaction.cancelled && this.product && this.product.type == 'service'"
          outlined color="primary" small elevation="0" @click="pauseDialog = true">
          {{ $t('message.request_pausing') }}
        </v-btn>
        <v-btn
          v-if="!hideCancellationButton && transaction.completed && !transaction.cancelled && this.product && this.product.type == 'service'"
          color="error" small elevation="0" @click="cancellationDialog = true">
          {{ $t('message.request_cancellation') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn small elevation="0" @click="navigateTo({ name: 'myacademytab', params: { tabId: 2 } })">
          {{ $t('message.back') }}
        </v-btn>
      </v-card-actions>
      <div v-else>
        <v-btn block v-if="transaction.completed && (!invoices || invoices.length == 0)" small elevation="0"
          @click="sendEmailDialog = true;" color="primary" outlined>
          {{ $t("message.email_receipt") }}
        </v-btn>
        <v-btn class="mt-1" block
          v-if="!hidePauseButton && transaction.completed && this.product && this.product.type == 'service'" outlined
          color="primary" small elevation="0" @click="pauseDialog = true">
          {{ $t('message.request_pausing') }}
        </v-btn>
        <v-btn class="mt-1" block
          v-if="!hideCancellationButton && transaction.completed && this.product && this.product.type == 'service'"
          color="error" small elevation="0" @click="cancellationDialog = true">
          {{ $t('message.request_cancellation') }}
        </v-btn>
        <v-btn class="mt-1" block small elevation="0"
          @click="navigateTo({ name: 'myacademytab', params: { tabId: 2 } })">
          {{ $t('message.back') }}
        </v-btn>
      </div>
    </v-sheet>
    <v-dialog v-model="cancellationDialog" width="90%">
      <v-card outlined>
        <v-card-title>
          {{ $t("message.request_cancellation") }}
        </v-card-title>
        <v-card-text>
          <v-textarea hide-details outlined v-model="cancelationReason" single-line outline
            :label="$t('message.reason')"></v-textarea>
          <div class="text-caption">
            <v-icon size="20">mdi-alert-outline</v-icon>
            {{ $t("message.request_cancellation_explained") }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small :disabled="cancelationReason.length < 5" color="error"
            @click="requestCancellation()">
            {{ $t("message.submit") }}
          </v-btn>
          <v-btn elevation="0" small @click="cancellationDialog = false;">
            {{ $t("message.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="pauseDialog" width="90%">
      <v-card outlined>
        <v-card-title>
          {{ $t("message.request_pausing") }}
        </v-card-title>
        <v-card-text>
          <v-textarea hide-details outlined v-model="pausingReason" single-line outline
            :label="$t('message.reason')"></v-textarea>
          <div class="text-caption">
            <v-icon size="20">mdi-alert-outline</v-icon>
            {{ $t("message.request_pause_explained") }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small :disabled="pausingReason.length < 5" color="error"
            @click="requestPausing()">
            {{ $t("message.submit") }}
          </v-btn>
          <v-btn elevation="0" small @click="pauseDialog = false;">
            {{ $t("message.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="sendEmailDialog" width="90%">
      <v-card outlined>
        <v-card-title>
          {{ $t("message.email_receipt") }}
        </v-card-title>
        <v-card-title>
          <v-switch v-model="altEmailConfirm" :label="$t('message.use_different_email')"></v-switch>
        </v-card-title>
        <v-card-title>
          <v-text-field dense outlined :disabled="!altEmailConfirm" single-line outline :label="$t('message.email')"
            v-model="altEmail"></v-text-field>
        </v-card-title>
        <v-card-actions>
          <v-btn elevation="0" small outlined :disabled="false" color="primary" @click="emailReceipt()">
            {{
              $t("message.email")
            }}
          </v-btn>
          <v-btn elevation="0" small @click="sendEmailDialog = false; selectedInvoice = null;">
            {{
              $t("message.close")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PaymentMethod from "@/components/displays/PaymentMethodDisplay.vue";
import UserAvatar from "@/components/displays/UserAvatarDisplay.vue";
import { Environment } from "@/services/Environment";
import FileService3 from "@/services/FileService3";
import PaymentsService from "@/services/PaymentsService";

//Icons
import {
  CreditCardIcon,
  DesktopComputerIcon,
  DeviceMobileIcon,
  LocationMarkerIcon,
  ReceiptTaxIcon,
  TagIcon,
} from '@vue-hero-icons/outline';

export default {
  data() {
    return {
      transactionId: this.$route.params.transactionId,
      queryError: this.$route.query.error,
      sessionId: this.$route.query.sessionId,
      sessionStatus: this.$route.query.status,
      checkoutType: this.$route.query.type,
      isIncompleteCustomAch: null,
      session: null,
      transaction: null,
      currencySymbol: "$",
      handlingCharges: false,
      stripeClientSecret: null,
      academy: null,
      loading: true,
      allowsBankPayment: false,
      product: null,
      payingLateMessage: null,
      subscriptionStatus: null,
      checkins: 0,
      amount: 0,
      mustUseBrowserToComplete: false,
      paymentMethod: null,
      altEmail: null,
      pendingUpdateCompletion: false,
      selectedInvoice: null,
      sendEmailDialog: null,
      transactionItems: [],
      totalAmount: 0,
      cancellationDialog: false,
      pauseDialog: false,
      hideCancellationButton: false,
      hidePauseButton: false,
      cancelationReason: "",
      pausingReason: "",
      altEmailConfirm: null,
      taxData: { enabled: false },
      customAchPostData: {},
      upcomingInvoice: null,
      invoices: null,
      intervals: [
        { text: this.$t("message.daily"), value: "day" },
        { text: this.$t("message.weekly"), value: "week" },
        { text: this.$t("message.monthly"), value: "month" },
        { text: this.$t("message.yearly"), value: "year" }
      ],
      uploadingFile: false,
      selectedFile: null,
      uploadPercentage: 0,
      uploadedFile: null,
      confirmingReceipt: false
    };
  },
  components: {
    DesktopComputerIcon,
    ReceiptTaxIcon,
    TagIcon,
    CreditCardIcon,
    PaymentMethod,
    LocationMarkerIcon,
    DeviceMobileIcon,
    UserAvatar
  },
  created() {
    //Get transaction, if not cancelled, proceed
    if (this.transactionId) {
      if (this.sessionId && this.sessionStatus == "success") {
        if (this.checkoutType && this.checkoutType == 'update')
          this.completeCreditCardUpdate();
      } else {
        this.loadTransaction(true);
        if (this.queryError)
          this.showAlert("error", this.$t("message.incomplete"));
      }
    }
  },
  methods: {
    loadEasyPayInvoices() {
      PaymentsService.getInvoices(this.transactionId)
        .then(response => {
          if (response) {
            this.invoices = response.invoices;
            this.upcomingInvoice = response.upcomingInvoice;
          }
        });
    },
    confirmUploadedReceipt() {
      if (this.uploadedFile) {
        this.confirmingReceipt = true;
        //Do the post
        PaymentsService.updateTransactionReceipt(this.transaction.managedAcademy._id, this.transaction._id, { receipt: this.uploadedFile })
          .then(response => {
            if (response) {
              this.transaction.receipt = this.uploadedFile;
              this.uploadedFile = null;
              this.confirmingReceipt = false;
              this.showAlert("success", this.$t("message.updated"));
            }
          })
          .catch(() => {
            this.confirmingReceipt = false;
            this.showAlert("error", this.$t("message.error_generic"));
          });
      }
    },
    filePicker() {
      this.$refs.fileUploadInput.click();
    },
    async uploadNewImage() {
      const vm = this;
      const formData = new FormData();
      const d = new Date();
      const millis = d.getTime();
      const imgExtension = this.selectedFile.type.toLowerCase().split('/')[1];
      const blob = new Blob([this.selectedFile], { type: this.selectedFile.type });
      formData.append("image", blob, 'bjjlink-user-receipt-uploaded-image-' + millis + '.' + imgExtension);
      //formData.append("image", this.selectedFile);
      const response = await FileService3.uploadImage(formData, function (progressEvent) {
        const percent = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        if (percent >= 97)
          vm.uploadPercentage = 97;//Stay on 97
        else
          vm.uploadPercentage = percent;
      });
      if (response && response.location) {
        setTimeout(function () {
          const newImage = response.location;
          vm.uploadingFile = false;
          vm.uploadedFile = newImage;
        }, 5000);
      }
    },
    onPhotoFileSelect(event) {
      this.uploadingFile = true;
      if (event.target.files[0]) {
        this.uploadedVideoFile = null;
        this.uploadedFile = null;
        this.selectedFile = event.target.files[0];
        const size = event.target.files[0].size;
        const sizeMb = Math.floor(size / 1000000);
        if (sizeMb <= 15) {
          this.uploadNewImage();
        } else {
          this.uploadingFile = false;
          this.showAlert("error", this.$t("message.image_too_big") + sizeMb + "mb");
        }
      } else {
        this.uploadingFile = false;
      }
    },
    removeReceipt(persist) {
      this.uploadedFile = null;
      this.$refs.fileUploadInput.value = null;
      if (this.transaction.receipt && persist) {
        PaymentsService.updateTransactionReceipt(this.transaction.managedAcademy._id, this.transaction._id, { receipt: null })
          .then(response => {
            if (response) {
              this.transaction.receipt = null;
            }
          })
          .catch(() => {
            this.showAlert("error", this.$t("message.error_generic"));
          });
      }
    },
    async processCustomAchTransaction() {
      this.handlingCharges = true;
      /*eslint-disable no-undef*/
      let stripe = Stripe(Environment.get("VUE_APP_STRIPE_PUBLISHABLE_KEY"), {
        stripeAccount: this.transaction.stripeAccountId
      });
      if (this.transaction.paymentType == 'acss_debit') {
        if (!this.stripeClientSecret) {
          this.showAlert("error", "Invalid data to complete ACH payment. Contact Suppport");
          this.handlingCharges = false;
          return;
        }
        let paymentIntentReturned = null;
        let setupIntentReturned = null;
        let errorReturned = null;
        if (this.stripeClientSecret.startsWith("seti_")) {
          const { setupIntent, error } = await stripe.confirmAcssDebitSetup(
            this.stripeClientSecret,
            {
              payment_method: {
                billing_details: {
                  name: this.transaction.buyer.displayName,
                  email: this.transaction.email,
                },
              },
            }
          );
          setupIntentReturned = setupIntent;
          errorReturned = error;
        } else {
          const { paymentIntent, error } = await stripe.confirmAcssDebitPayment(
            this.stripeClientSecret,
            {
              payment_method: {
                billing_details: {
                  name: this.transaction.buyer.displayName,
                  email: this.transaction.email,
                },
              },
            }
          );
          paymentIntentReturned = paymentIntent;
          errorReturned = error;
        }
        if (errorReturned) {
          console.error('Error processing acss_debit: ', errorReturned);
          this.handlingCharges = false;
          this.showAlert("error", errorReturned.message, null, 7000);
        } else if (paymentIntentReturned && paymentIntentReturned.status != 'canceled') {
          let postData = {};
          if (this.transaction.subscriptionStartDate)
            postData.addMandateFee = true;
          this.completeCustomAchPurchase(postData);
        } else if (setupIntentReturned && setupIntentReturned.status != 'canceled') {
          let postData = { setupIntent: setupIntentReturned.id, paymentMethodId: setupIntentReturned.payment_method };
          if (this.transaction.subscriptionStartDate) {
            postData.addMandateFee = true;
          }
          this.completeCustomAchPurchase(postData);
        }
      } else if (this.transaction.paymentType == 'nz_bank_account') {
        if (!this.customAchPostData.account_number ||
          !this.customAchPostData.bank_code ||
          !this.customAchPostData.branch_code ||
          !this.customAchPostData.suffix) {
          this.showAlert("error", "Please complete all bank information");
          this.handlingCharges = false;
          return;
        }
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: 'nz_bank_account',
          nz_bank_account: {
            account_number: this.customAchPostData.account_number, // 7-digit account base
            bank_code: this.customAchPostData.bank_code, // 2 digits
            branch_code: this.customAchPostData.branch_code,     // 4-digit branch code
            suffix: this.customAchPostData.suffix
          },
          billing_details: {
            name: this.transaction.buyer.displayName,
            email: this.transaction.email,
          },
        });
        if (error) {
          console.error('Error processing acss_debit: ', error.message);
          this.handlingCharges = false;
          this.showAlert("error", error.message, null, 7000);
        } else if (paymentMethod) {
          this.handlingCharges = true;
          //This payment methos is ready to be approved for mandate
          let postData = { paymentMethodConfirmed: false, paymentMethodId: paymentMethod.id };
          if (this.transaction.subscriptionStartDate) {
            postData.addMandateFee = true;
          }
          this.completeCustomAchPurchase(postData);
        }
      } else {
        this.showAlert("error", "Invalid ACH payment type: ", this.selectedPaymentOptions);
        this.handlingCharges = false
        return;
      }
    },
    completeCustomAchPurchase(data) {
      PaymentsService.completeCustomAchTransaction(this.transactionId, data)
        .then(response => {
          if (response) {
            const vm = this;
            setTimeout(function () {
              vm.showAlert("success", vm.$t("message.success"));
              vm.loadTransaction();
              vm.handlingCharges = false;
              vm.isIncompleteCustomAch = null;
            }, 1000);
          }
        })
        .catch(() => {
          this.loadTransaction();
          this.handlingCharges = false;
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    requestCancellation() {
      PaymentsService.requestCancellation(this.transactionId, this.cancelationReason)
        .then(response => {
          if (response)
            this.showAlert("success", this.$t("message.email_sent_to_academy"));
          this.cancellationDialog = false;
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    requestPausing() {
      PaymentsService.requestPausing(this.transactionId, this.pausingReason)
        .then(response => {
          if (response)
            this.showAlert("success", this.$t("message.email_sent_to_academy"));
          this.pauseDialog = false;
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    completePurchase(silent, sessionId) {
      let id = null;
      if (sessionId)
        id = sessionId;
      else
        id = this.session.id;
      if (!id && !silent) {
        this.showAlert("error", this.$t("message.error_generic"));
        return;
      }
      PaymentsService.complete(id)
        .then(response => {
          if (response) {
            /*
            const vm = this;
            setTimeout(function () {
              vm.loadTransaction();
            }, 1000);
            */
          }
        })
        .catch(() => {
          if (!silent)
            this.showAlert("error", this.$t("message.something_went_wrong_but_wait"), null, 8000);
        });
    },
    loadTransaction(init) {
      return PaymentsService.transaction(this.transactionId)
        .then(response => {
          if (response.force_complete) {
            if (response.sessionId) {
              this.pendingUpdateCompletion = true;
              this.sessionId = response.sessionId;
            } else {
              this.showAlert("error", "Cant complete purchase, ontact academy. CODE SETUPSESS_PENDING");
              this.loading = false;
              return;
            }
          }
          this.transaction = response.transaction;
          this.hideCancellationButton = response.hideCancellationRequestButton;
          this.hidePauseButton = response.hidePauseRequestButton;
          if (this.transaction.product) {
            //WE now display the product or products always as a list
            this.transactionItems = [
              {
                product: this.transaction.product,
                amountDecimal: this.transaction.product.amountDecimal,
                quantity: 1
              }
            ];
          }
          this.stripeClientSecret = response.stripeClientSecret;
          if (this.stripeClientSecret && !this.transaction.completed) {
            const endDate = new Date(this.transaction.createdAt);
            endDate.setDate(endDate.getDate() + 1);
            this.isIncompleteCustomAch = endDate;
          }
          if (this.transaction.recurrence) {
            this.totalAmount = this.transaction.recurrence.totalAmount;
          } else {
            this.totalAmount = this.transaction.amount * this.transaction.quantity
          }
          if (this.transaction.items && this.transaction.items.length) {
            for (let i of this.transaction.items) {
              this.transactionItems.push({
                product: i.product,
                amountDecimal: i.amountDecimal,
                name: i.name,
                description: i.description,
                quantity: i.quantity ? i.quantity : 1
              });
            }
          }
          if (this.$store.state.inApp && this.transaction.paymentType == 'acss_debit')
            this.mustUseBrowserToComplete = true;
          this.session = response.session ? response.session : { status: 'inactive' };
          if (!this.transaction.completed && init)
            this.completePurchase(true);

          if (this.transaction.managedAcademy && this.transaction.managedAcademy.currencySymbol)
            this.currencySymbol = this.transaction.managedAcademy.currencySymbol;
          if (response.automaticTax) {
            this.taxData = {
              enabled: true,
              totalDetails: response.totalDetails
            };
          }
          this.academy = response.academy;
          this.checkins = response.checkins;
          this.product = this.transaction.product;
          this.subscriptionStatus = response.status;
          if (!this.transaction.completed && !this.transaction.cancelled && this.product && this.product.type == "service") {
            const d2 = this.toEndOfDay(new Date());
            const d1 = new Date(this.transaction.time);
            const daysBetween = Math.abs(this.daysBetween(d1, d2));
            if (daysBetween > 1) {
              const dateDisplay = this.$d(new Date(this.transaction.time), 'month_day', this.$i18n.locale)
              this.payingLateMessage = this.$t("message.pay_subcription_late_explained", [daysBetween, dateDisplay]);
            }
          }
          this.invoices = response.invoices;
          if (this.transaction.paymentsModule == 'easypay')
            this.loadEasyPayInvoices();
          this.amount = this.transaction.amount ? this.transaction.amount : 0;
          this.paymentMethod = response.paymentMethod;
          this.loading = false;
          const paymentTypes = this.transaction.paymentTypes;
          if (paymentTypes && paymentTypes.length > 0) {
            for (const pt of paymentTypes) {
              if (pt.indexOf('bank') >= 0) {
                this.allowsBankPayment = true;
                return;
              }
            }
          }
        })
        .catch(e => {
          console.log(e)
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
          this.loading = false;
        });
    },
    completeCheckout() {
      if (this.transaction.paymentsModule == 'infinicept') {
        this.navigateTo({
          name: "inifnicept-checkout",
          params: {
            transactionId: this.transactionId,
            managedAcademyId: this.managedAcademy,
          }
        });
      } else if (this.transaction.paymentsModule == 'easypay') {
        this.navigateTo({
          name: "easypay-checkout-transaction",
          params: {
            transactionId: this.transactionId,
            managedAcademyId: this.managedAcademy,
          }
        });
      } else if (this.isCustomAchTransaction(this.transaction)) {
        if (this.$store.state.inApp && this.transaction.paymentType == 'acss_debit') {
          this.showAlert("error", 'Please complete this transaction by logging in through a browser instead of the app.');
          return;
        }
        this.processCustomAchTransaction();
      } else {
        this.navigateTo({
          name: "checkout",
          params: { transactionId: this.transactionId },
        });
      }
    },
    completeCreditCardUpdate() {
      this.handlingCharges = true;
      PaymentsService.completeCreditCardUpdate(this.transactionId, this.sessionId)
        .then(response => {
          if (response) {
            setTimeout(() => {
              this.showAlert("success", this.$t("message.success"));
              this.handlingCharges = false;
              this.isIncompleteCustomAch = null;
              this.pendingUpdateCompletion = false;
              this.loadTransaction().then(() => {
                const invoices = this.invoices || [];
                const pastDueInvoice = invoices.find(i => i.amountPaid === 0 && i.attempts >= 1);
                if (pastDueInvoice) {
                  PaymentsService.retryCardPayment(this.transactionId, pastDueInvoice.paymentIntent)
                    .then(() => this.loadTransaction())
                    .catch(() => this.loadTransaction());
                }
              });
            }, 2000);
          }
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.loading = false;
          this.loadTransaction();
        });
    },
    updateCreditCard() {
      PaymentsService.initUpdateCreditCardSession(this.transactionId)
        .then(response => {
          const session = response.session;
          this.navigateTo({
            name: "ccupdate",
            params: { transactionId: this.transactionId },
            query: { s: session.id }
          });
        })
        .catch(e => {
          if (e) {
            if (e.response.data && e.response.data.message)
              this.showAlert("error", e.response.data.message);
            else
              this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    getCheckinsLeft() {
      let num = 0;
      if (this.transaction.subscriptionCheckins) {
        let checkins = this.transaction.checkins ? this.transaction.checkins.length : 0;
        num = this.transaction.subscriptionCheckins - checkins;
        if (num < 0)
          num = 0;
      }
      return num;
    },
    emailReceipt() {
      let invoiceData = null;
      let invoiceId = null;
      if (this.transaction.paymentsModule == 'easypay')
        invoiceData = { invoice: this.selectedInvoice };
      else
        invoiceId = this.selectedInvoice;
      PaymentsService.emailReceipt(this.transactionId, this.altEmail, invoiceId, invoiceData)
        .then(() => {
          this.sendEmailDialog = false;
          this.selectedInvoice = null;
          this.showAlert("info", this.$t("message.success"));
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    goToAcademy(id) {
      this.navigateTo({ name: "viewacademy", params: { academyId: id } });
    },
    displayInterval() {
      for (const i of this.intervals) {
        if (i.value == this.product.interval) return i.text;
      }
    },
  }
};
</script>

<style scoped>
.expired {
  color: #EC7063;
  font-size: medium;
}
</style>
